(() => {
  if (window.AeockyAdvancedSlideshowInitialized) {
    return;
  }

  window.AeockyAdvancedSlideshowInitialized = true;

  const ROOT_SELECTOR = '[data-ae-slideshow]';
  const TRACK_SELECTOR = '[data-ae-scroll-sequence]';
  const SLIDE_SELECTOR = ':scope > [data-ae-slideshow-slide]';
  const MOBILE_QUERY = '(max-width: 699px)';
  const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
  const NAVIGATION_START_EVENT = 'aeocky:pdp-navigation-start';
  const NAVIGATION_END_EVENT = 'aeocky:pdp-navigation-end';
  const MODEL_CONTENT_ANIMATION_MS = 700;
  const SCROLL_INDEX_HYSTERESIS = 0.06;
  const controllers = new WeakMap();
  const connectedControllers = new Set();

  class AeockySlideshowController {
    constructor(root) {
      this.root = root;
      this.track = root.closest(TRACK_SELECTOR);
      this.section = root.closest('.shopify-section--advanced-slideshow');
      this.carousel = root.querySelector('slideshow-carousel');
      this.abortController = new AbortController();
      this.resizeObserver = null;
      this.scrollFrame = null;
      this.measureFrame = null;
      this.isReady = false;
      this.isConnected = false;
      this.isSelectingFromScroll = false;
      this.navigationBypass = false;
      this.pendingIndex = null;
      this.scrollIndex = null;
      this.lastScrollPosition = window.scrollY;
      this.modelContentAnimationTimer = null;
      this.stepDistance = 0;
      this.transitionDistance = 0;
      this.sequenceStart = 0;
      this.sequenceEnd = 0;
      this.lastViewportWidth = window.innerWidth;
      this.lastViewportHeight = window.visualViewport?.height || window.innerHeight;
      this.stickyOffset = 0;
      this.mobileMedia = window.matchMedia(MOBILE_QUERY);
      this.reducedMotionMedia = window.matchMedia(REDUCED_MOTION_QUERY);

      this.handleScroll = this.handleScroll.bind(this);
      this.handleViewportChange = this.handleViewportChange.bind(this);
      this.handleMediaChange = this.handleMediaChange.bind(this);
      this.handleCarouselChange = this.handleCarouselChange.bind(this);
      this.handleNavigationStart = this.handleNavigationStart.bind(this);
      this.handleNavigationEnd = this.handleNavigationEnd.bind(this);
      this.handlePlayerResume = this.handlePlayerResume.bind(this);
    }

    get slides() {
      return this.carousel ? Array.from(this.carousel.querySelectorAll(SLIDE_SELECTOR)) : [];
    }

    connect() {
      if (!this.carousel || !this.track || this.isConnected) {
        return;
      }

      this.isConnected = true;
      const { signal } = this.abortController;

      if (this.carousel.dataset.pauseOnHover === 'true') {
        this.carousel.addEventListener('mouseenter', () => this.carousel.player?.pause(), { signal });
        this.carousel.addEventListener('mouseleave', () => {
          if (!this.isReady) {
            this.carousel.player?.resume();
          }
        }, { signal });
        this.carousel.addEventListener('focusin', () => this.carousel.player?.pause(), { signal });
        this.carousel.addEventListener('focusout', (event) => {
          if (!this.carousel.contains(event.relatedTarget) && !this.isReady) {
            this.carousel.player?.resume();
          }
        }, { signal });
      }

      this.carousel.addEventListener('carousel:change', this.handleCarouselChange, { signal });
      window.addEventListener('scroll', this.handleScroll, { passive: true, signal });
      window.addEventListener('resize', this.handleViewportChange, { passive: true, signal });
      window.visualViewport?.addEventListener('resize', this.handleViewportChange, { passive: true, signal });
      window.addEventListener('orientationchange', this.handleMediaChange, { signal });
      window.addEventListener('load', this.handleMediaChange, { once: true, signal });
      document.addEventListener(NAVIGATION_START_EVENT, this.handleNavigationStart, { signal });
      document.addEventListener(NAVIGATION_END_EVENT, this.handleNavigationEnd, { signal });
      this.mobileMedia.addEventListener?.('change', this.handleMediaChange, { signal });
      this.reducedMotionMedia.addEventListener?.('change', this.handleMediaChange, { signal });

      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(() => this.scheduleMeasure());
        this.resizeObserver.observe(this.root);
      }

      window.customElements.whenDefined('slideshow-carousel').then(() => {
        if (!this.isConnected) {
          return;
        }

        this.carousel.player?.addEventListener('player:resume', this.handlePlayerResume, { signal });
        this.scheduleMeasure();
      });

      document.fonts?.ready.then(() => this.scheduleMeasure());
      this.scheduleMeasure();
    }

    disconnect() {
      if (!this.isConnected) {
        return;
      }

      this.isConnected = false;
      this.abortController.abort();
      this.resizeObserver?.disconnect();
      this.mobileMedia.removeEventListener?.('change', this.handleMediaChange);
      this.reducedMotionMedia.removeEventListener?.('change', this.handleMediaChange);
      window.cancelAnimationFrame(this.scrollFrame);
      window.cancelAnimationFrame(this.measureFrame);
      window.clearTimeout(this.modelContentAnimationTimer);
      this.deactivate(false);
    }

    shouldEnable() {
      if (this.track.dataset.scrollSequenceEnabled !== 'true'
        || this.slides.length < 2
        || this.reducedMotionMedia.matches) {
        return false;
      }

      return !this.mobileMedia.matches || this.track.dataset.scrollSequenceMobile === 'true';
    }

    scheduleMeasure() {
      if (this.measureFrame !== null) {
        return;
      }

      this.measureFrame = window.requestAnimationFrame(() => {
        this.measureFrame = null;
        this.measure();
      });
    }

    measure() {
      if (!this.shouldEnable()) {
        this.deactivate();
        return;
      }

      const configuredStickyOffset = this.getStickyOffset();
      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      const slideshowHeight = this.root.getBoundingClientRect().height;

      if (viewportHeight <= 0 || slideshowHeight <= 0) {
        this.track.dataset.scrollSequenceFallback = 'invalid-geometry';
        this.deactivate();
        return;
      }

      delete this.track.dataset.scrollSequenceFallback;
      // On short windows, preserve the full slide instead of disabling the
      // story or cropping its media. The sticky offset is the only value that
      // is reduced when the slideshow is taller than the available space.
      const maximumStickyOffset = Math.max(0, viewportHeight - slideshowHeight);
      const stickyOffset = Math.max(0, Math.min(configuredStickyOffset, maximumStickyOffset));
      const availableHeight = Math.max(1, viewportHeight - stickyOffset);

      if (stickyOffset < configuredStickyOffset - 1) {
        this.track.dataset.scrollSequenceViewport = 'constrained';
      } else {
        delete this.track.dataset.scrollSequenceViewport;
      }

      const configuredDistance = Number(this.track.dataset.scrollSequenceDistance) || 35;
      const minimumDistance = this.mobileMedia.matches ? 220 : 240;
      const maximumDistance = this.mobileMedia.matches ? 360 : 420;
      this.stepDistance = Math.max(
        minimumDistance,
        Math.min(maximumDistance, Math.round(availableHeight * configuredDistance / 100))
      );
      this.transitionDistance = this.stepDistance * Math.max(0, this.slides.length - 1);
      const releaseDistance = Math.max(100, Math.min(180, Math.round(this.stepDistance * 0.35)));
      const scrollDistance = this.transitionDistance + releaseDistance;
      const trackHeight = Math.ceil(slideshowHeight + scrollDistance);

      this.stickyOffset = stickyOffset;
      this.track.style.setProperty('--ae-scroll-sequence-offset', `${Math.round(stickyOffset)}px`);
      this.track.style.setProperty('--ae-scroll-sequence-track-height', `${trackHeight}px`);
      this.track.classList.add('is-scroll-sequence-ready');
      this.section?.classList.add('is-ae-scroll-sequence-ready');
      this.isReady = true;

      const trackTop = this.track.getBoundingClientRect().top + window.scrollY;
      this.sequenceStart = trackTop - stickyOffset;
      this.sequenceEnd = this.sequenceStart + scrollDistance;
      this.lastScrollPosition = window.scrollY;
      this.scrollIndex = null;
      this.pauseAutoplay();
      this.updateFromScroll(false);
    }

    deactivate(resumeAutoplay = true) {
      const wasReady = this.isReady;
      this.isReady = false;
      this.pendingIndex = null;
      this.scrollIndex = null;
      this.isSelectingFromScroll = false;
      this.clearModelContentAnimation();
      this.track?.classList.remove('is-scroll-sequence-ready');
      this.track?.style.removeProperty('--ae-scroll-sequence-offset');
      this.track?.style.removeProperty('--ae-scroll-sequence-track-height');
      delete this.track?.dataset.scrollSequenceViewport;
      this.section?.classList.remove('is-ae-scroll-sequence-ready');
      this.stickyOffset = 0;

      if (wasReady && resumeAutoplay && this.carousel?.hasAttribute('autoplay')) {
        this.carousel.player?.resume(true);
      }
    }

    handleScroll() {
      if (!this.isReady || this.navigationBypass || this.scrollFrame !== null) {
        return;
      }

      this.scrollFrame = window.requestAnimationFrame(() => {
        this.scrollFrame = null;
        if (this.hasStickyOffsetChanged()) {
          this.measure();
          return;
        }

        this.updateFromScroll(true);
      });
    }

    getCurrentIndex() {
      const selectedIndex = Number(this.carousel?.selectedIndex);
      if (Number.isInteger(selectedIndex) && selectedIndex >= 0) {
        return Math.max(0, Math.min(this.slides.length - 1, selectedIndex));
      }

      const selectedSlideIndex = this.slides.findIndex((slide) => slide.classList.contains('is-selected'));
      return selectedSlideIndex >= 0 ? selectedSlideIndex : 0;
    }

    handleViewportChange() {
      const widthChanged = Math.abs(window.innerWidth - this.lastViewportWidth) > 1;
      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      const heightChanged = Math.abs(viewportHeight - this.lastViewportHeight) > 1;
      if (!widthChanged && !heightChanged) {
        return;
      }

      this.lastViewportWidth = window.innerWidth;
      this.lastViewportHeight = viewportHeight;
      this.scheduleMeasure();
    }

    handleMediaChange() {
      this.lastViewportWidth = window.innerWidth;
      this.lastViewportHeight = window.visualViewport?.height || window.innerHeight;
      this.scheduleMeasure();
    }

    hasStickyOffsetChanged() {
      const configuredStickyOffset = this.getStickyOffset();
      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      const slideshowHeight = this.root.getBoundingClientRect().height;
      const maximumStickyOffset = Math.max(0, viewportHeight - slideshowHeight);
      const nextStickyOffset = Math.max(0, Math.min(configuredStickyOffset, maximumStickyOffset));

      return Math.abs(nextStickyOffset - this.stickyOffset) > 1;
    }

    updateFromScroll(animate) {
      if (!this.isReady || this.navigationBypass || this.stepDistance <= 0) {
        return;
      }

      const scrollPosition = window.scrollY;
      const direction = Math.sign(scrollPosition - this.lastScrollPosition);
      const lastIndex = this.slides.length - 1;
      const relativeScroll = Math.max(0, scrollPosition - this.sequenceStart);
      const rawIndex = relativeScroll / this.stepDistance;
      let index = this.scrollIndex;

      if (scrollPosition <= this.sequenceStart) {
        index = 0;
      } else if (relativeScroll >= this.transitionDistance) {
        index = lastIndex;
      } else if (!Number.isInteger(index)) {
        index = Math.max(0, Math.min(lastIndex, Math.floor(rawIndex)));
      } else if (direction > 0) {
        while (index < lastIndex && rawIndex >= index + 1 + SCROLL_INDEX_HYSTERESIS) {
          index += 1;
        }
      } else if (direction < 0) {
        while (index > 0 && rawIndex <= index - SCROLL_INDEX_HYSTERESIS) {
          index -= 1;
        }
      }

      this.lastScrollPosition = scrollPosition;
      this.scrollIndex = index;
      const insideSequence = scrollPosition > this.sequenceStart && scrollPosition < this.sequenceEnd;
      this.selectIndex(index, animate && insideSequence);
    }

    getContentItems(index) {
      const content = this.slides[index]?.querySelector('.ae-slideshow__content');
      return content
        ? Array.from(content.children).filter((element) => !['STYLE', 'SCRIPT', 'TEMPLATE'].includes(element.tagName))
        : [];
    }

    getContentSignature(element) {
      const text = element.textContent?.replace(/\s+/g, ' ').trim() || '';
      const media = Array.from(element.querySelectorAll('img, video, source'))
        .map((item) => item.currentSrc || item.src || item.getAttribute('src') || '')
        .join('|');
      return `${text}|${media}`;
    }

    prepareModelContentAnimation(fromIndex, toIndex) {
      const animationMode = this.track.dataset.scrollSequenceContentAnimation || 'none';
      if (animationMode === 'none' || this.reducedMotionMedia.matches) {
        return null;
      }

      this.clearModelContentAnimation();
      const fromItems = this.getContentItems(fromIndex);
      const toItems = this.getContentItems(toIndex);
      const changedFrom = [];
      const changedTo = [];
      const itemCount = Math.max(fromItems.length, toItems.length);

      for (let index = 0; index < itemCount; index += 1) {
        const fromItem = fromItems[index];
        const toItem = toItems[index];
        const fromSignature = fromItem ? this.getContentSignature(fromItem) : '';
        const toSignature = toItem ? this.getContentSignature(toItem) : '';

        if (fromSignature === toSignature) {
          continue;
        }

        if (fromItem) {
          fromItem.classList.add('is-ae-model-content-leaving');
          fromItem.style.setProperty('--ae-model-content-order', changedFrom.length);
          changedFrom.push(fromItem);
        }

        if (toItem) {
          toItem.style.setProperty('--ae-model-content-order', changedTo.length);
          changedTo.push(toItem);
        }
      }

      if (changedFrom.length === 0 && changedTo.length === 0) {
        return null;
      }

      this.root.classList.add('is-ae-model-content-changing');
      return { changedFrom, changedTo };
    }

    finishModelContentAnimation(animationState) {
      if (!animationState) {
        return;
      }

      animationState.changedFrom.forEach((element) => {
        element.classList.remove('is-ae-model-content-leaving');
      });

      animationState.changedTo.forEach((element) => {
        element.classList.add('is-ae-model-content-entering');
      });

      window.clearTimeout(this.modelContentAnimationTimer);
      this.modelContentAnimationTimer = window.setTimeout(() => {
        this.clearModelContentAnimation();
      }, MODEL_CONTENT_ANIMATION_MS);
    }

    clearModelContentAnimation() {
      window.clearTimeout(this.modelContentAnimationTimer);
      this.modelContentAnimationTimer = null;
      this.root.classList.remove('is-ae-model-content-changing');
      this.root.querySelectorAll('.ae-slideshow__content > *').forEach((element) => {
        element.classList.remove('is-ae-model-content-leaving', 'is-ae-model-content-entering');
        element.style.removeProperty('--ae-model-content-order');
      });
    }

    selectIndex(index, animate) {
      if (!this.carousel || typeof this.carousel.select !== 'function') {
        return;
      }

      if (this.isSelectingFromScroll) {
        this.pendingIndex = index;
        return;
      }

      if (this.getCurrentIndex() === index) {
        this.pauseAutoplay();
        return;
      }

      const previousIndex = this.getCurrentIndex();
      const contentAnimation = animate ? this.prepareModelContentAnimation(previousIndex, index) : null;
      this.isSelectingFromScroll = true;
      this.pendingIndex = null;
      // Impact's fade_with_text transition throws when animate:false is used.
      // Keep its short native transition while native page scroll remains free.
      const selection = this.carousel.select(index, { animate: true });

      Promise.resolve(selection).finally(() => {
        this.isSelectingFromScroll = false;
        this.pauseAutoplay();
        this.finishModelContentAnimation(contentAnimation);

        if (this.pendingIndex !== null && this.pendingIndex !== this.getCurrentIndex()) {
          const pendingIndex = this.pendingIndex;
          this.pendingIndex = null;
          this.selectIndex(pendingIndex, animate);
        }
      });
    }

    handleCarouselChange() {
      // Dot/arrow controls remain usable, but they never move or lock the page.
      // The next native scroll event synchronizes the slide to scroll progress.
      this.pauseAutoplay();
    }

    handleNavigationStart() {
      // PDP navigation is an explicit destination request. Let its own native
      // scroll complete without the slideshow changing slides underneath it.
      this.navigationBypass = true;
      this.pendingIndex = null;
      this.clearModelContentAnimation();
      window.cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = null;
    }

    handleNavigationEnd() {
      this.navigationBypass = false;
      this.scheduleMeasure();
      window.requestAnimationFrame(() => this.updateFromScroll(false));
    }

    handlePlayerResume() {
      if (this.isReady) {
        window.requestAnimationFrame(() => this.pauseAutoplay());
      }
    }

    pauseAutoplay() {
      if (this.isReady) {
        this.carousel?.player?.pause();
      }
    }

    getStickyOffset() {
      const tabsSection = document.querySelector('.shopify-section--product-2026-tabs');
      if (tabsSection) {
        const tabsTop = Number.parseFloat(window.getComputedStyle(tabsSection).top) || 0;
        return Math.max(0, tabsTop) + tabsSection.getBoundingClientRect().height + 12;
      }

      const documentStyles = window.getComputedStyle(document.documentElement);
      const stickyHeaderEnabled = Number.parseFloat(documentStyles.getPropertyValue('--sticky-header-enabled')) || 0;
      const stickyAnnouncementEnabled = Number.parseFloat(documentStyles.getPropertyValue('--sticky-announcement-bar-enabled')) || 0;
      const headerHeight = Number.parseFloat(documentStyles.getPropertyValue('--header-height')) || 0;
      const announcementHeight = Number.parseFloat(documentStyles.getPropertyValue('--announcement-bar-height')) || 0;

      return (stickyHeaderEnabled * headerHeight) + (stickyAnnouncementEnabled * announcementHeight);
    }
  }

  const initializeCarousel = (root) => {
    if (!root || controllers.has(root)) {
      return;
    }

    const controller = new AeockySlideshowController(root);
    controllers.set(root, controller);
    connectedControllers.add(controller);
    controller.connect();
  };

  const initializeAll = (root = document) => {
    if (root.matches?.(ROOT_SELECTOR)) {
      initializeCarousel(root);
    }

    root.querySelectorAll?.(ROOT_SELECTOR).forEach(initializeCarousel);
  };

  const destroyAll = (root) => {
    connectedControllers.forEach((controller) => {
      if (root === controller.root || root.contains?.(controller.root)) {
        controller.disconnect();
        connectedControllers.delete(controller);
        controllers.delete(controller.root);
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(), { once: true });
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyAll(event.target));
  document.addEventListener('shopify:section:select', (event) => {
    initializeAll(event.target);
    event.target.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => controllers.get(root)?.scheduleMeasure());
  });
  document.addEventListener('shopify:section:reorder', () => {
    connectedControllers.forEach((controller) => controller.scheduleMeasure());
  });

  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target.closest?.('[data-ae-slideshow-slide]');
    const carousel = slide?.closest('slideshow-carousel');

    if (!slide || !carousel) {
      return;
    }

    const slides = Array.from(carousel.querySelectorAll(SLIDE_SELECTOR));
    const index = slides.indexOf(slide);

    if (index >= 0 && typeof carousel.select === 'function') {
      carousel.select(index, { animate: !event.detail.load });
    }
  });
})();
